/**
 * The single status → colour vocabulary (ENG-1744).
 *
 * Before this module the app carried ~60 independent status→class tables. The
 * same `failed` rendered six ways, `cancelled` at three different opacities,
 * and `meshing` pulsed on the mesh tree while sitting still in the design-point
 * table. This file replaces the *colour* half of all of them.
 *
 * Two layers, and the split is load-bearing:
 *
 *   1. A closed `Tone` vocabulary, rendered through five element roles — chip,
 *      fill, text, banner surface and outline ring. One recipe per role per
 *      tone; the roles are not derivable from each other (see each table's
 *      note).
 *   2. Small **namespaced** maps, one per status vocabulary, resolving a domain
 *      key to a tone.
 *
 * A single flat key→class table is not merely undesirable, it is lossy:
 * `error`, `success` and `warning` are simultaneously status keys *and* tone
 * names, and `info` is the least-severe rung of the validation triple (where it
 * must read grey) as well as the name of the blue tone. Namespacing is what
 * lets both be true.
 *
 * **Namespaces disambiguate; they do not license divergence.** Any key that
 * appears in more than one namespace and means the same state must resolve to
 * the same tone, and `statusTone.test.ts` proves it. That test carries exactly
 * one exemption; adding a second needs a reviewer, not a keystroke.
 *
 * The WORDS live next door, in `statusLabels.ts`, split the same way and for
 * the same reason.
 *
 * Key off the backend enum, or off an explicit declaration — never off a
 * TypeScript type's *name*. Several unions in this app are named `Tone` or
 * `ChipTone` while their members are design-point statuses, and vice versa.
 */
import type {
  DesignPointStatus,
  GeometryStatus,
  MeshStatus,
  OrgMemberStatus,
  ReadinessTier,
  SimulationStatus,
  StageReport,
  StudyStatus,
} from '@/api/generated/types.gen';
import type { DiagnosisTagTone } from '@/components/mesh/diagnosisTags';
import type { RepairOutcome } from '@/components/mesh/RepairHistory';
import type { SnapSeverity } from '@/components/mesh/snapFidelity';
import type { DashboardGeometryStatus } from '@/hooks/useDashboardGeometries';
import type { DiagnosisLibraryItemState } from '@/hooks/useDiagnosisLibrary';
import type { PhaseState } from '@/routes/_app/simops/-components/design-points/PhaseTrack';
import type { TileStatus } from '@/routes/_app/simops/-components/evaluate/SandboxPanel';
import type { TaskState } from '@/routes/_app/simops/-components/files/GeometryDropzone';
import type { DpMeshingStatus } from '@/routes/_app/simops/-components/meshing/types';
import type { RunOutcome } from '@/routes/_app/simops/-data/run-outcome';
import type { ValidationLevel } from '@/utils/meshValidation';
import { cn } from '@/lib/utils';

/**
 * The closed tone vocabulary: five status tones, plus one product-identity tone
 * that no status ever resolves to. A seventh entry is a request for a new
 * meaning, not a new colour.
 *
 * `diagnosis` is the odd one out on purpose. `--diagnosis` aliases `--primary`
 * (ENG-876) and has no `-strong` sibling, and it means "this is the Diagnosis
 * product", not "this job is in some state". So no namespace map below resolves
 * to it — `statusTone.test.ts` proves that, and `statusTone()` therefore never
 * returns it — and it is reachable only by an explicit declaration at a call
 * site: today just `DiagnosisVerdictCard`'s mesh-ready banner, where the tone
 * is the product's own identity rather than a verdict. The one *status* that
 * used to render diagnosis-coloured (`diagnosing`) is an in-progress state and
 * resolves to `info` like every other one.
 */
export type Tone =
  | 'success'
  | 'info'
  | 'warning'
  | 'destructive'
  | 'muted'
  | 'diagnosis';

/**
 * READ THIS BEFORE "FIXING" THE TEXT TOKENS BELOW.
 *
 * **A text token on a tint is the `-strong` sibling. A fill is the base
 * token.** That is the whole rule, and it is why the tables below look
 * inconsistent with each other on purpose: `TONE_BADGE` and `TONE_TEXT` emit
 * `-strong`, while `TONE_FILL` and `TONE_RING` emit the base token and
 * `TONE_BANNER` emits no text token at all. Do not "harmonise" them.
 *
 * ENG-1744 shipped this file with `warning` alone on `-strong`, because
 * `--destructive-strong`, `--success-strong` and `--info-strong` did not exist
 * yet and writing them would have compiled, linted clean and rendered
 * colourless. ENG-1734/1735 added them (`src/index.css`, all three themes), so
 * the deferred swap is done and all four tones now read their sibling.
 *
 * **Why every one of them needed it, measured per surface.** A tint is
 * transparent, so the surface under it is part of the colour; a token has to
 * clear the *worst* cell a theme paints it over, not a convenient one. Light
 * paints `bg-muted`/`bg-accent`/`bg-secondary` at 0.97 and `.dark` paints them
 * at 0.269 — a hovered or selected row, and the hardest ground in each. On the
 * chip's own `/10` tint over those, the **base** tokens read:
 *
 *     :root    success 4.16   info 4.19   destructive 3.67   warning 1.65
 *     .dark    success 3.69   info 3.59   destructive 4.56   warning 6.47
 *
 * against AA's 4.5. The `-strong` siblings read 5.35 / 5.37 / 5.58 / 5.22 and
 * 5.81 / 5.61 / 5.79 / 6.47. In `.theme-academic` every base value is already
 * dark enough and three of the four siblings collapse to `var(--<token>)`, so
 * the same class names stay correct there (worst cell 5.78).
 *
 * The figures that made three of these look fine in ENG-1744 —
 * `destructive` 4.76, `info` 5.26, `success` 5.20 — are the tokens **opaque on
 * white**, which is the one measurement that never applies to text sitting on
 * its own tint. AGENTS.md → Theming carries the same warning.
 *
 * `muted` takes no sibling: ENG-1734 retunes `--muted-foreground` itself
 * (0.556 → 0.54) rather than adding a strong variant, so `text-muted-foreground`
 * is already the compliant token.
 *
 * Bare `text-warning` on surfaces *outside* a status chip is ENG-1461's
 * separate sweep, not this module's business.
 */

/**
 * The bordered `/10` recipe — the single spelling for a status chip, badge or
 * pill.
 *
 * The border never carries a state distinction on its own — measured,
 * `border-destructive/20` over its own `/10` fill is about 1.4:1 in every
 * theme. Every chip must also be identified by its label and, where present,
 * its icon.
 */
const TONE_BADGE: Record<Tone, string> = {
  success: 'border border-success/30 bg-success/10 text-success-strong',
  info: 'border border-info/30 bg-info/10 text-info-strong',
  warning: 'border border-warning/30 bg-warning/10 text-warning-strong',
  destructive:
    'border border-destructive/30 bg-destructive/10 text-destructive-strong',
  muted: 'border border-border bg-muted text-muted-foreground',
  // No status resolves here, so this row exists for completeness — and
  // `--diagnosis` has no `-strong` sibling, because it is never read on its own
  // tint as a status.
  diagnosis: 'border border-diagnosis/30 bg-diagnosis/10 text-diagnosis',
};

/**
 * Solid fills — dots, progress bars, grid cells, solid pills.
 *
 * A fill is not text, so this table stays on the **base** token in every tone:
 * `-strong` here would be wrong, and `cancelled` is full opacity with no alpha
 * (it previously appeared at three different opacities on four surfaces).
 *
 * This is a different job from `TONE_BADGE` even though both are keyed by
 * `Tone`. Do not merge them.
 */
const TONE_FILL: Record<Tone, string> = {
  success: 'bg-success',
  info: 'bg-info',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  muted: 'bg-muted-foreground',
  diagnosis: 'bg-diagnosis',
};

/**
 * Bare text, on the page rather than on a tint — a status word beside a
 * timestamp, a tree-row label, a validation glyph.
 *
 * A third role, and it is not a pill without a background: a word rendered on
 * `bg-background` and the same word rendered on `bg-warning/10` are different
 * contrast problems, which is why the role gets its own table rather than being
 * derived from `TONE_BADGE`.
 *
 * Every tone takes `-strong` here for the same reason it does on the chip —
 * this role is read, not filled. The tint is gone, so the ratios are the easier
 * ones, but they are not automatically safe: on the hardest surface each theme
 * paints (light 0.97, `.dark` 0.269) the **base** tokens read `success` 4.77 /
 * 4.12 and `info` 4.82 / 4.02, which straddle AA rather than clearing it, and
 * `warning` reads 1.75. The siblings read 6.13 / 6.50, 6.18 / 6.28 and 5.54 /
 * 7.90. See the note above `TONE_BADGE`.
 */
const TONE_TEXT: Record<Tone, string> = {
  success: 'text-success-strong',
  info: 'text-info-strong',
  warning: 'text-warning-strong',
  destructive: 'text-destructive-strong',
  muted: 'text-muted-foreground',
  diagnosis: 'text-diagnosis',
};

/**
 * The `/5` tinted **surface** — a callout, a banner, a severity card, an upload
 * row.
 *
 * **Why this is `/5` where `TONE_BADGE` is `/10`, and why that is not an
 * inconsistency waiting to be unified.** A chip is a small shape carrying its
 * own label, so its tint has to read as a figure against the page. A banner is
 * a full-width card whose tint is the *ground* under body copy that stays
 * `text-muted-foreground`. Doubling the tint under a whole card is a visible
 * change to every callout in the app, not a normalisation. Both depths already
 * ship and both were measured: chip `/10`, banner `/5`, and one border at
 * `/30` in either role because the border is chrome, not state (see
 * `TONE_BADGE`).
 *
 * The other difference: this table emits **surface only**, no text token. A
 * chip's text and tint are the same element; a banner's heading is a child, and
 * its body copy deliberately is not toned at all. Compose `toneTextClass(tone)`
 * on the heading, which is what brings the `-strong` token to a banner: this
 * `/5` is the shallowest tint in the file, so it is also the one where a base
 * token looks most defensible and is not.
 */
const TONE_BANNER: Record<Tone, string> = {
  success: 'border border-success/30 bg-success/5',
  info: 'border border-info/30 bg-info/5',
  warning: 'border border-warning/30 bg-warning/5',
  destructive: 'border border-destructive/30 bg-destructive/5',
  // `muted` has no alpha ladder to take a `/5` from — `--muted` *is* the
  // low-contrast surface — so the neutral banner is the neutral chip's surface.
  muted: 'border border-border bg-muted',
  diagnosis: 'border border-diagnosis/30 bg-diagnosis/5',
};

/**
 * The outline **ring** — the fifth element role, and the only one that paints
 * nothing of its own: it flashes around an element that already exists, to say
 * "the message you just clicked is about *this* field".
 *
 * This is a status colour, not a focus affordance. Both call sites reached for
 * it precisely because the focus token `ring-ring` was the wrong signal — it is
 * one neutral colour, and the point here is that the ring reads as the same
 * severity as the chip the user clicked. A real focus ring stays `ring-ring`
 * and has no business in this table.
 *
 * `warning` stays on the **base** token: a ring is not text, so this table
 * follows `TONE_FILL` rather than `TONE_TEXT` and takes no `-strong`. The
 * caller owns the ring's width, offset and any pulse — this emits colour only,
 * because the two sites disagree about width on purpose (a 2px ring on a form
 * input, a 4px one on a button that is already visible).
 */
const TONE_RING: Record<Tone, string> = {
  success: 'ring-success',
  info: 'ring-info',
  warning: 'ring-warning',
  destructive: 'ring-destructive',
  muted: 'ring-muted-foreground',
  diagnosis: 'ring-diagnosis',
};

/**
 * The two role tables that emit a **text** token, exposed for
 * `statusTone.test.ts`. Not for rendering — call `toneBadgeClass()` /
 * `toneTextClass()`.
 *
 * The accessors return `cn(TABLE[tone])`, and tailwind-merge collapses a
 * duplicate utility before any caller can observe it: an entry spelling
 * `text-success text-success-strong` renders — and therefore *asserts* — as
 * `text-success-strong` alone. So the "no base token beside its `-strong`
 * sibling" guard cannot be written against rendered output; it has to read the
 * raw table value, which is what this export is for.
 *
 * `TONE_FILL` and `TONE_RING` emit `bg-`/`ring-`, and `TONE_BANNER` emits no
 * text token at all, so none of them are in scope for that guard.
 */
export const TONE_TEXT_TABLES: Readonly<
  Record<'badge' | 'text', Readonly<Record<Tone, string>>>
> = {
  badge: TONE_BADGE,
  text: TONE_TEXT,
};

// ---------------------------------------------------------------------------
// Namespaced status → tone maps
//
// Each is an exhaustive `Record<…, Tone>` over its vocabulary, so a new backend
// status fails the build rather than rendering untinted.
// ---------------------------------------------------------------------------

/** `MeshStatus` — MeshGen + SimOps mesh jobs. */
const MESH: Record<MeshStatus, Tone> = {
  pending: 'muted',
  meshing: 'info',
  recombining: 'info',
  completed: 'success',
  failed: 'destructive',
  cancelled: 'muted',
};

/**
 * `StageReport['status']` — one stage inside a mesh run's pipeline timeline.
 *
 * This namespace is why namespacing exists: `success` and `warning` are tone
 * names *and* wire values here. `timeout` joins `failed` because a stage that
 * ran out of wall clock did not produce a mesh.
 *
 * The timeline's `skipped` marker is a hollow ring rather than a fill, so it is
 * the one row that does not render through `toneDotClass` — see `StageDot`.
 */
const MESH_STAGE: Record<StageReport['status'], Tone> = {
  running: 'info',
  success: 'success',
  skipped: 'muted',
  failed: 'destructive',
  timeout: 'destructive',
  warning: 'warning',
};

/** `SimulationStatus` — one solver run. */
const SIMULATION: Record<SimulationStatus, Tone> = {
  pending: 'muted',
  // `queued` means "not started yet", exactly like `pending`. The eval surface
  // rendered it blue, which now reads as in-progress; muted is the codebase's
  // dominant treatment and the honest one.
  queued: 'muted',
  running: 'info',
  completed: 'success',
  failed: 'destructive',
  cancelled: 'muted',
  // Stopped at its iteration or time limit with a checkpoint (ENG-2864):
  // nothing went wrong, but the run has not settled and waits on the reader to
  // continue it, so `stopped` reads `warning`, as the detail header's chip
  // does (ENG-2935).
  // Dead row: readers map `paused` to `stopped` first; the contract step deletes it.
  paused: 'muted',
  stopped: 'warning',
};

/** `DesignPointStatus` — the mesh→simulate→extract lifecycle of one DP. */
const DESIGN_POINT: Record<DesignPointStatus, Tone> = {
  pending: 'muted',
  preprocessing: 'info',
  meshing: 'info',
  // `mesh_ready` is warning because it is waiting on the user, and
  // `faces_done` is the success milestone past it. These are distinct states;
  // `faces_done` is NOT the `done` alias below.
  mesh_ready: 'warning',
  faces_done: 'success',
  simulating: 'info',
  extracting: 'info',
  pending_review: 'warning',
  completed: 'success',
  failed: 'destructive',
  cancelled: 'muted',
};

/** `StudyStatus` — a whole study. */
const STUDY: Record<StudyStatus, Tone> = {
  draft: 'muted',
  preprocessing: 'info',
  awaiting_user_meshing: 'warning',
  awaiting_bc_review: 'warning',
  awaiting_mobo_review: 'warning',
  running: 'info',
  completed: 'success',
  completed_with_issues: 'warning',
  failed: 'destructive',
  cancelled: 'muted',
};

/** `DpMeshingStatus` — frontend-only; the SimOps user-meshing bridge. */
const DP_MESHING: Record<DpMeshingStatus, Tone> = {
  not_started: 'muted',
  meshing: 'info',
  mesh_ready: 'warning',
  faces_done: 'success',
  failed: 'destructive',
};

/**
 * `DashboardGeometryStatus` — frontend-only; the MeshGen library card.
 *
 * `meshing` was amber here and blue everywhere else. It is an in-progress
 * state, so it joins the rest on `info`.
 */
const DASHBOARD_GEOMETRY: Record<DashboardGeometryStatus, Tone> = {
  processing: 'info',
  error: 'destructive',
  no_mesh: 'muted',
  meshing: 'info',
  done: 'success',
  failed: 'destructive',
};

/**
 * `PhaseState` — frontend-only; one leg of the DP phase track.
 *
 * `skipped` resolves to `muted` for *text and chips*. The progress board's cell
 * rail takes `active`, `done`, `failed` and `cancelled` from this map and keeps
 * two local rows, because there `idle` is the empty track and `skipped` is the
 * absence of a cell rather than a state with a colour — `cellClass` in
 * `StudyProgressBoard.tsx` carries the full reasoning.
 */
const PHASE: Record<PhaseState, Tone> = {
  idle: 'muted',
  active: 'info',
  done: 'success',
  failed: 'destructive',
  skipped: 'muted',
  cancelled: 'muted',
};

/**
 * `GeometryStatus` — the lifecycle of one uploaded geometry version.
 *
 * This namespace closes the `repairing` gap: `repairing` was already in
 * `IN_PROGRESS_KEYS` but in no map at all, so `DiagnosisFileTree` had to
 * declare its pulsing dot locally. Like every other in-flight state it resolves
 * to `info` — the amber it used to render is the same divergence
 * `dashboardGeometry.meshing` had, and `awaiting_approval` is the state here
 * that is genuinely waiting on the user, so that is the one that reads amber.
 *
 * Distinct from `diagnosisLibrary`, which is a frontend card state and spells
 * the *needs*-repair verdict `repair`; this one is the wire enum.
 */
const GEOMETRY: Record<GeometryStatus, Tone> = {
  idle: 'muted',
  diagnosing: 'info',
  repairing: 'info',
  extracting: 'info',
  awaiting_approval: 'warning',
  error: 'destructive',
};

/** `DiagnosisLibraryItemState` — frontend-only; the Diagnosis library. */
const DIAGNOSIS_LIBRARY: Record<DiagnosisLibraryItemState, Tone> = {
  diagnosing: 'info',
  failed: 'destructive',
  needs_approval: 'warning',
  ready: 'success',
  // `repair` and `failed` share a tone but are distinct states — a repairable
  // geometry is not a failed diagnosis. Both keys must survive; do not dedupe
  // this map by value.
  repair: 'destructive',
  wrap: 'info',
  // Not a failure of ours and not in progress: Diagnosis is refused for a
  // missing seat or subscription (ENG-3091).
  refused: 'warning',
};

/** `DiagnosisTagTone` — the three-tier CAD-damage severity scale. */
const DIAGNOSIS_TAG: Record<DiagnosisTagTone, Tone> = {
  // `critical` maps to `destructive` for completeness, but its pill is a
  // *filled* shape with a text-shadow halo, not a tint — see `diagnosisTags.ts`.
  // Do not render it through `toneBadgeClass`.
  critical: 'destructive',
  blocking: 'destructive',
  advisory: 'warning',
  clean: 'success',
};

/**
 * Geometry repair outcome. No generated enum exists — the value arrives as a
 * bare `string` — so the union is an explicit declaration next to the component
 * that receives it, and this map is exhaustive over that.
 */
const REPAIR_OUTCOME: Record<RepairOutcome, Tone> = {
  RESTORED: 'success',
  PARTIAL: 'warning',
  UNREPAIRABLE: 'destructive',
  TERMINAL: 'destructive',
};

/** `ReadinessTier` — can this geometry be meshed. */
const READINESS_TIER: Record<ReadinessTier, Tone> = {
  ready: 'success',
  ready_with_warnings: 'warning',
  not_ready: 'destructive',
};

/** Snap-deviation advisory gate. */
const SNAP_SEVERITY: Record<SnapSeverity, Tone> = {
  pass: 'success',
  warn: 'warning',
  fail: 'destructive',
};

/**
 * `RunOutcome` — the terminal word before a run's elapsed time.
 *
 * Verification is a stronger claim than the provisional Converged label.
 */
const RUN_OUTCOME: Record<RunOutcome, Tone> = {
  verified: 'success',
  // Provisional Converged covers completed runs without a full Verified
  // pass, including measured NOT_CONVERGED / iteration-limit stops that
  // did not diverge (ENG-3044). Quality lives in the verification panel.
  converged: 'info',
  diverged: 'destructive',
  failed: 'destructive',
  // Time-limit stop (ENG-2935 / ENG-2864) — same state, and so the same
  // tone, as `simulation.stopped`. Iteration-limit stops classify as
  // Converged instead.
  stopped: 'warning',
};

/**
 * `ValidationLevel` — an ordered severity triple, not a tone triple. `info` is
 * the *least severe* rung and reads grey, which is why this namespace exists:
 * a flat table would have to pick between grey and blue for the same word.
 */
const VALIDATION_LEVEL: Record<ValidationLevel, Tone> = {
  error: 'destructive',
  warning: 'warning',
  info: 'muted',
};

/** `OrgMemberStatus` — account / team membership. */
const ORG_MEMBER: Record<OrgMemberStatus, Tone> = {
  invited: 'warning',
  // `active` here means "neither suspended nor invited". A member who is fine
  // is not "in progress", so this is one of the two `active`s that are not
  // `info` — `subscription.active` is the other, for the same reason. Both sit
  // under the `active` entry in the cross-namespace agreement test's exemption.
  active: 'success',
  suspended: 'destructive',
};

/** Geometry upload task rows. */
const UPLOAD: Record<TaskState['status'], Tone> = {
  uploading: 'info',
  done: 'success',
  error: 'destructive',
};

/** EvalTool sandbox tiles — how well-backed each plot is. */
const EVAL_TILE: Record<TileStatus, Tone> = {
  live: 'success',
  needsHand: 'warning',
  underDevelopment: 'info',
  notEvaluated: 'muted',
};

/**
 * What the AI retry loop did to one design point, derived by
 * `simops/-components/evaluate/ai-retry-log.ts`. Frontend-only vocabulary, like
 * `runOutcome` and `evalTile` — it is here rather than beside its component so
 * the cross-namespace agreement walk in `statusTone.test.ts` can see it, and so
 * "in progress reads `info`" stays one decision rather than nineteen.
 */
const AI_RETRY_OUTCOME = {
  recovered: 'success',
  retrying: 'info',
  gave_up: 'muted',
  declined: 'muted',
} as const satisfies Record<string, Tone>;

/**
 * Stripe's `subscription.status`, for the six values worth showing (ENG-1146,
 * ENG-1233).
 *
 * Declared here rather than generated from a backend enum because there is no
 * enum to generate from: `SubscriptionResponse.status` is `str | None` on the
 * wire, the raw Stripe string mirrored into `subscriptions.status`. Stripe's
 * own vocabulary is closed and documented, so this is a *subset* of eight, not
 * a guess — the two omitted (`incomplete`, `incomplete_expired`) say nothing a
 * reader could act on, and the panel renders no chip for them. Treat an
 * unrecognised string the same way, via `subscriptionStatusKey`.
 *
 * `unpaid` joined the set in ENG-1233. It used to be in the omitted list for
 * the same reason as the other two, and that reading stopped being true when
 * it became the dunning terminal: it is now the one status that says the
 * organization has lost access, which is the most actionable thing this chip
 * can report.
 *
 * `canceled` is Stripe's American spelling of the `cancelled` every other
 * namespace here uses, which is why `STATUS_ALIASES` carries it: the two are
 * one state and must stay one tone.
 */
const SUBSCRIPTION = {
  // See `DIVERGENCE_EXEMPT` in the test: a subscription that is `active` is
  // healthy, not a job in flight, so it takes membership's `success` rather
  // than the `info` every running-job namespace gives the same word.
  active: 'success',
  trialing: 'info',
  // The org still has access — `past_due` is inside `LIVE_STATUSES` — but the
  // last payment failed and the owner has to act, which is the whole reason
  // this chip exists.
  past_due: 'warning',
  // The end of the retry window: Stripe gave up, the backend revoked every
  // grant, and the organization holds nothing until the card is fixed.
  // `destructive` rather than `past_due`'s `warning` — that one warns about
  // something coming, this one reports something that has happened.
  unpaid: 'destructive',
  canceled: 'muted',
  paused: 'muted',
} as const satisfies Record<string, Tone>;

const TONE_BY_NAMESPACE = {
  mesh: MESH,
  meshStage: MESH_STAGE,
  simulation: SIMULATION,
  designPoint: DESIGN_POINT,
  study: STUDY,
  dpMeshing: DP_MESHING,
  dashboardGeometry: DASHBOARD_GEOMETRY,
  phase: PHASE,
  geometry: GEOMETRY,
  diagnosisLibrary: DIAGNOSIS_LIBRARY,
  diagnosisTag: DIAGNOSIS_TAG,
  readinessTier: READINESS_TIER,
  repairOutcome: REPAIR_OUTCOME,
  snapSeverity: SNAP_SEVERITY,
  runOutcome: RUN_OUTCOME,
  validationLevel: VALIDATION_LEVEL,
  orgMember: ORG_MEMBER,
  upload: UPLOAD,
  evalTile: EVAL_TILE,
  aiRetryOutcome: AI_RETRY_OUTCOME,
  subscription: SUBSCRIPTION,
} as const;

/** Every status vocabulary this module knows how to tone. */
export type StatusNamespace = keyof typeof TONE_BY_NAMESPACE;

/** The keys valid for one namespace. */
export type StatusKey<N extends StatusNamespace> =
  keyof (typeof TONE_BY_NAMESPACE)[N] & string;

/**
 * Exposed for `statusTone.test.ts`, which walks every namespace to prove
 * cross-namespace agreement. Not for rendering — call `statusTone()`.
 */
export const STATUS_TONE_MAPS: {
  readonly [N in StatusNamespace]: Readonly<Record<string, Tone>>;
} = TONE_BY_NAMESPACE;

/**
 * Aliases — a frontend spelling that means the same state as a canonical key.
 *
 * `done` appears in **zero** backend enums; it is a frontend-only spelling of
 * `completed` and must resolve to the same tone. `faces_done` merely contains
 * the substring and is a distinct `DesignPointStatus` milestone — never
 * collapse it into `done`.
 *
 * This is a contract over the maps above, enforced by `statusTone.test.ts`, not
 * a runtime redirect: `StatusKey<N>` already stops a caller passing `done` to a
 * namespace that spells it `completed`.
 */
export const STATUS_ALIASES: Readonly<Record<string, string>> = {
  done: 'completed',
  // Stripe spells it with one `l`. Same state, same tone — see `SUBSCRIPTION`.
  canceled: 'cancelled',
};

/**
 * Namespaces whose vocabulary describes a STATE, never a job in flight.
 *
 * `IN_PROGRESS_KEYS` below is keyed on the bare word, so any namespace that
 * happens to reuse one of those words has to be subtracted again here:
 * `orgMember.active` is a membership that is neither invited nor suspended,
 * and `subscription.active` is a contract in good standing. Neither pulses.
 * `subscription.trialing` is the near miss — a trial is a phase of a healthy
 * contract, so it reads `info` without being work.
 *
 * A set rather than a chain of `||` so the list is data a reviewer sees and a
 * test can assert closed. If it reaches four, the deeper fix is to key
 * in-progress on the (namespace, key) pair instead of the word, which removes
 * the need to subtract anything.
 */
const NON_WORK_NAMESPACES: ReadonlySet<StatusNamespace> = new Set([
  'orgMember',
  'subscription',
]);

/**
 * Status keys that denote work actually in flight. These are the states that
 * pulse — replacing both the old static dots and the `animate-spin` spinner, so
 * one idiom means one thing.
 */
const IN_PROGRESS_KEYS: ReadonlySet<string> = new Set([
  'meshing',
  'recombining',
  'running',
  'preprocessing',
  'simulating',
  'extracting',
  'processing',
  'diagnosing',
  'repairing',
  'uploading',
  'active',
]);

/** Resolve a domain status to its tone. */
export function statusTone<N extends StatusNamespace>(
  namespace: N,
  key: StatusKey<N>,
): Tone {
  // `TONE_BY_NAMESPACE` is an object literal too, so the namespace axis needs
  // the same guard as the key axis below — this is one lookup, not two. A
  // namespace that defeated `StatusNamespace` and is spelled `constructor`
  // resolves to a *function*; `__proto__` resolves to `Object.prototype`,
  // which owns `constructor`, `toString` and the rest, so the key guard below
  // then *finds* them and hands back a function from a namespace that does not
  // exist. An unrecognised namespace yields the empty table instead, so every
  // key in it falls back to `muted` exactly like an unrecognised key.
  const map: Readonly<Record<string, Tone | undefined>> = Object.hasOwn(
    TONE_BY_NAMESPACE,
    namespace,
  )
    ? TONE_BY_NAMESPACE[namespace]
    : {};
  // A wire value outside the generated enum (backend deployed ahead of the
  // client) resolves to `muted` rather than rendering an untinted chip.
  //
  // `Object.hasOwn`, not `map[key] ?? …`: these maps are object literals, so a
  // bare lookup walks the prototype chain. A key spelled `constructor`,
  // `toString`, `valueOf` — any `Object.prototype` member — returns a
  // *function*, which is truthy, so `??` never fires and the fallback that
  // exists precisely for unrecognised wire values is the one thing that does
  // not happen. The renderers then hand `cn()` a function, which it drops, and
  // the untinted chip ships. Same guard as `RepairStatusBadge`'s outcome
  // lookup, and `statusTone.test.ts` walks every prototype member name.
  return Object.hasOwn(map, key) ? (map[key] ?? 'muted') : 'muted';
}

/**
 * Does this status represent work in flight, i.e. should its indicator pulse?
 */
export function statusInProgress<N extends StatusNamespace>(
  namespace: N,
  key: StatusKey<N>,
): boolean {
  if (NON_WORK_NAMESPACES.has(namespace)) return false;
  return IN_PROGRESS_KEYS.has(key);
}

/**
 * Read one row out of a tone table under the same `hasOwn` guard `statusTone`
 * uses.
 *
 * `Tone` is a compile-time promise and these tables are object literals, so an
 * unvalidated string that reaches a role — a cast, a value crossing a JSON
 * boundary, a caller that skipped `statusTone` — indexes straight onto
 * `Object.prototype`. `cn()` drops a function, and drops an object with no
 * enumerable keys, so the result is `''`: an element with no tint, no border
 * and no text colour, which is invisible rather than merely wrong. Falling
 * back to the neutral row keeps such a value visible-but-neutral, exactly like
 * an unrecognised status.
 */
function toneRow(table: Readonly<Record<Tone, string>>, tone: Tone): string {
  return Object.hasOwn(table, tone) ? table[tone] : table.muted;
}

/**
 * The bordered `/10` chip recipe for a tone — border colour + tint + text.
 *
 * Returns tone classes only; the caller owns the pill geometry (radius,
 * padding, type size), which differs legitimately between a dense table chip
 * and a card badge. Compose it over `Badge` / `badgeVariants` rather than
 * re-inventing that geometry.
 *
 * Do **not** put this on an ancestor that carries `opacity-*`: the wrapper
 * re-dims the text below AA. And a container toned with this must not give its
 * children colour classes of their own — a per-child tone lookup is the bug
 * ENG-1744 removed.
 */
export function toneBadgeClass(tone: Tone): string {
  return cn(toneRow(TONE_BADGE, tone));
}

/**
 * The text-only colour for a tone — no tint, no border.
 *
 * Use this where the status is a word on the page, not a chip. Never put it on
 * an element that also carries `opacity-*`: the wrapper re-dims the text below
 * AA, and the tone class cannot see that it happened.
 */
export function toneTextClass(tone: Tone): string {
  return cn(toneRow(TONE_TEXT, tone));
}

/**
 * The `/5` tinted surface for a banner, callout or severity card — border
 * colour and tint, nothing else.
 *
 * Pair it with `toneTextClass(tone)` on the heading and leave the body copy
 * muted. Do **not** reach for `toneBadgeClass` on a surface like this: that is
 * the chip recipe at twice the tint depth, and swapping one for the other
 * repaints every callout in the app. `TONE_BANNER` explains why the two depths
 * differ.
 */
export function toneBannerClass(tone: Tone): string {
  return cn(toneRow(TONE_BANNER, tone));
}

/**
 * The outline-ring colour for a tone — colour only, no width, offset or
 * animation.
 *
 * Use it where a ring carries **severity**, i.e. it is telling the user which
 * field a validation message refers to. An ordinary focus ring is `ring-ring`
 * and is not a status; do not route that through here.
 */
export function toneRingClass(tone: Tone): string {
  return cn(toneRow(TONE_RING, tone));
}

export interface ToneDotOptions {
  /**
   * Pulse the fill. Reduced-motion users get the static dot: the pulse is
   * behind `motion-safe:`, which is new behaviour and an improvement on both
   * the unguarded `animate-pulse` and the `animate-spin` it replaces.
   */
  pulse?: boolean;
}

/** The solid-fill class for a dot, bar or grid cell. */
export function toneDotClass(tone: Tone, opts: ToneDotOptions = {}): string {
  return cn(
    toneRow(TONE_FILL, tone),
    opts.pulse === true && 'motion-safe:animate-pulse',
  );
}
